import React, { useEffect, useState } from 'react'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { GO_DE_GQL_BACKEND_URL } from '../../../constants'
import { useAuth } from '../../../contexts/AuthContext'
import { QUERY_TELEGRAM_MESSAGES } from '../Courses/query';
import { FetchApi } from '../../../api/fetch';

export interface IMessage {
  id: string;
  text: string;
  createdAt: string;
  attachment: string;
  recipient: string;
  recipientIds: string[]
  actions: string;
}

interface PaginationInfo {
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentOffset: number;
}

function TelegramMessages() {
  const { setIsLoadingOverlayDisplay } = useAuth()
  const [messages, setMessages] = useState<IMessage[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
    currentOffset: 0
  })

  const MESSAGES_PER_PAGE = 10

  useEffect(() => {
    getMessages()
  }, [])

  const getMessages = async (page: number = 1, showLoading: boolean = true) => {
    if (showLoading) {
      setIsLoadingOverlayDisplay(true)
    }

    const offset = (page - 1) * MESSAGES_PER_PAGE

    try {
      const result = await FetchApi({
        endpoint: GO_DE_GQL_BACKEND_URL,
        method: "POST",
        body:  ({
          query: QUERY_TELEGRAM_MESSAGES,
          variables: {
            limit: MESSAGES_PER_PAGE,
            offset: offset,
            orderBy: "-createdAt"
          }
        })
      })

      const nodes = result.data.telegramMessage.nodes
      const fetchedCount = nodes.length

      setMessages(nodes)
      setPagination({
        currentPage: page,
        hasNextPage: fetchedCount === MESSAGES_PER_PAGE, // If we got full limit, there might be more
        hasPreviousPage: page > 1,
        currentOffset: offset
      })
    } catch (error) {
      console.log(error);
    }

    if (showLoading) {
      setIsLoadingOverlayDisplay(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1) {
      getMessages(newPage)
    }
  }

  const renderPaginationButtons = () => {
    const buttons = []

    // Previous button
    if (pagination.hasPreviousPage) {
      buttons.push(
        <button
          key="prev"
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          className="border border-kyzzen-secondary px-4 pr-2 py-2 rounded-lg"
        >
          <ArrowBackIos />
        </button>
      )
    }

    // Current page indicator
    const pageText = pagination.hasNextPage ? `${pagination.currentPage} of many` : `${pagination.currentPage} of ${pagination.currentPage}`
    buttons.push(
      <div key="current" >
        {pageText}
      </div>
    )

    // Next button
    if (pagination.hasNextPage) {
      buttons.push(
        <button
          key="next"
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          className="border border-kyzzen-secondary px-4 py-2 rounded-lg"
        >
          <ArrowForwardIos />
        </button>
      )
    }

    return buttons
  }

  const formatButton = (button: string) => {
    const buttons: { text: string, value: string }[] = JSON.parse(button);

    if (buttons && buttons.length) {
      return (
        <div>
          {
            buttons.map((button) => (
              <p className='text-sm' key={button.text + button.value}><b className='font-semibold'>Button ({button.value.startsWith('/') ? 'Command' : 'Redirect'}) : </b>{button.text} = {button.value}</p>
            ))
          }
        </div>
      )

    } else {
      return <div></div>
    }

  }

  return (
    <div className='w-11/12 m-auto my-6'>
      <div className=" flex flex-row items-center justify-between flex-col sm:flex-row">
        <div className="w-auto">
          <h1 className="font-semibold text-3xl text-left mt-8 mb-8">
            Telegram
          </h1>
        </div>
      </div>

      {/* Messages list */}
      {messages.map((message) => (
        <div key={message.id} className="bg-gray-90 flex items-start justify-between p-4 my-4 rounded-lg">
          <div className='flex items-start w-full gap-3'>
            <div className="flex items-start w-full gap-3">
              <div
                className="w-[230px] rounded-md"
                style={{
                  backgroundImage: `url("${message?.attachment || "/placeholder.svg"}")`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <img src={message?.attachment || "/placeholder.svg"} alt="" className='w-full max-h-60 object-cover object-center-bottom rounded-md' />
              </div>
              <div className='w-full'>
                <p className="font-extralight text-sm text-gray-40 mb-2">
                  {new Date(message?.createdAt).toDateString()}
                </p>
                <p className='text-md font-normal capitalize mb-2'>Sent To: {message.recipient}</p>

                <p className="font-extralight text-sm text-gray-40 mb-2 max-h-30 overflow-hidden" dangerouslySetInnerHTML={{ __html: message.text }}>
                </p>
                <p>{formatButton(message.actions)}</p>

              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Empty state */}
      {messages.length < 1 && (
        <p className='text-xl text-center'>No Messages Sent...</p>
      )}

      {/* Pagination controls */}
      {(pagination.hasNextPage || pagination.hasPreviousPage) && (
        <div className="flex items-center flex-wrap justify-end gap-4">
          {renderPaginationButtons()}
        </div>
      )}
    </div>
  )
}

export default TelegramMessages